import type {
  AiUsageItinerary,
  AiUsageRole,
  AiUsageSession,
  AiUsageTool,
} from '../api/use-ai-usage-report';

type PhaseTotals = {
  clientRead: number;
  clientWritten: number;
  ourUsd: number;
};

export type SessionTotals = {
  sessions: number;
  itineraries: number;
  toolCalls: number;
  brainstorm: PhaseTotals;
  itinerary: PhaseTotals;
  clientTokens: number;
  ourUsd: number;
  apiUsd: number;
  totalUsd: number;
};

/** A runtime row has no client model in the loop. */
export const hasClientModel = (row: {
  apiEquivalentUsd: number | null;
}): boolean => row.apiEquivalentUsd !== null;

const zeroPhase = (): PhaseTotals => ({
  clientRead: 0,
  clientWritten: 0,
  ourUsd: 0,
});

export const sumSessions = (rows: readonly AiUsageSession[]): SessionTotals => {
  const totals: SessionTotals = {
    sessions: rows.length,
    itineraries: 0,
    toolCalls: 0,
    brainstorm: zeroPhase(),
    itinerary: zeroPhase(),
    clientTokens: 0,
    ourUsd: 0,
    apiUsd: 0,
    totalUsd: 0,
  };
  for (const row of rows) {
    const client = hasClientModel(row);
    totals.itineraries += row.itineraries.length;
    totals.toolCalls += row.toolCalls;
    for (const phase of ['brainstorm', 'itinerary'] as const) {
      if (client) {
        totals[phase].clientRead += row[phase].clientRead;
        totals[phase].clientWritten += row[phase].clientWritten;
      }
      totals[phase].ourUsd += row[phase].ourUsd;
    }
    if (client) totals.clientTokens += row.clientTokens;
    totals.ourUsd += row.ourUsd;
    totals.apiUsd += row.apiEquivalentUsd ?? 0;
    totals.totalUsd += row.totalUsd;
  }
  return totals;
};

export type ItineraryTotals = {
  itineraries: number;
  clientTokens: number;
  ourUsd: number;
  apiUsd: number;
  totalUsd: number;
};

/** A shared session's figures repeat on each of its itineraries: count them once. */
export const sumItineraries = (
  rows: readonly AiUsageItinerary[]
): ItineraryTotals => {
  const totals: ItineraryTotals = {
    itineraries: rows.length,
    clientTokens: 0,
    ourUsd: 0,
    apiUsd: 0,
    totalUsd: 0,
  };
  const seen = new Set<string>();
  for (const row of rows) {
    if (seen.has(row.runId)) continue;
    seen.add(row.runId);
    if (hasClientModel(row)) totals.clientTokens += row.clientTokens;
    totals.ourUsd += row.ourUsd;
    totals.apiUsd += row.apiEquivalentUsd ?? 0;
    totals.totalUsd += row.totalUsd;
  }
  return totals;
};

export const toolTokens = (row: AiUsageTool): number =>
  row.clientRead + row.clientWritten;

export const sortToolsByTokens = (
  rows: readonly AiUsageTool[]
): AiUsageTool[] => [...rows].sort((a, b) => toolTokens(b) - toolTokens(a));

export const sumTools = (rows: readonly AiUsageTool[]) =>
  rows.reduce(
    (acc, row) => ({
      calls: acc.calls + row.calls,
      clientRead: acc.clientRead + row.clientRead,
      clientWritten: acc.clientWritten + row.clientWritten,
    }),
    { calls: 0, clientRead: 0, clientWritten: 0 }
  );

export const sortRolesByUsd = (rows: readonly AiUsageRole[]): AiUsageRole[] =>
  [...rows].sort((a, b) => b.usd - a.usd);

export const sumRoles = (rows: readonly AiUsageRole[]) =>
  rows.reduce(
    (acc, row) => ({
      calls: acc.calls + row.calls,
      usd: acc.usd + row.usd,
      tokens: acc.tokens + row.tokens,
    }),
    { calls: 0, usd: 0, tokens: 0 }
  );
